import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Check, CreditCard, Home, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { needsOnboarding, useAuth } from "@/context/AuthContext";
import { InkCharacter } from "@/components/InkCharacter";
import { Wordmark } from "@/components/Brand";
import { PartnerCodeField, PartnerPlanCard, fmtDate } from "@/components/PartnerCode";
import { AccountSections } from "@/components/AccountSections";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "");
const PERKS = ["Unlimited guided L.O.V.E. pigment consultations", "Your verified pigment library and kit-first suggestions", "Label scanning, bottle photos and your shelf-style kit", "Consultation reports for your records"];

const money = (n) => `$${Number(n).toFixed(2)}`;
const per = (plan) => (plan === "yearly" ? "year" : "month");

async function goCheckout(setBusy, plan) {
  setBusy(true);
  try {
    const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin, plan });
    window.location.href = data.checkout_url;
  } catch (e) {
    toast.error(errMsg(e));
    setBusy(false);
  }
}

function Perks() {
  return (
    <ul className="space-y-3">
      {PERKS.map((p) => (
        <li key={p} className="flex items-start gap-3 text-sm text-[#4A3B32]">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#C5A059]/20 text-[#9C6218]"><Check size={13} strokeWidth={2.5} /></span>{p}
        </li>
      ))}
    </ul>
  );
}

export function SubscribePage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(() => (localStorage.getItem("ig-plan") === "monthly" ? "monthly" : "yearly"));
  const b = user?.billing || {};
  if (b.partner?.active) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#FDFBF7] px-6 text-center" data-testid="subscribe-partner-active">
        <InkCharacter size="md" />
        <p className="mt-8 max-w-lg font-serif text-3xl font-semibold leading-snug text-[#2C1810]" data-testid="partner-active-message">
          Your PMU Accelerator partner access is active through {fmtDate(b.partner.end_date)}. Enjoy Ink Genius!
        </p>
        <button onClick={() => nav(user.onboarding_completed_at ? "/" : "/onboarding")} data-testid="partner-continue-btn" className="mt-8 inline-flex h-12 items-center rounded-full bg-[#C5A059] px-8 text-sm font-semibold text-[#2C1810] hover:bg-[#d4b273]">Continue</button>
      </div>
    );
  }
  if (b.has_access && !b.grace_until) return <Navigate to="/" replace />;
  const lapsed = ["canceled", "unpaid", "incomplete_expired", "past_due", "incomplete"].includes(b.status) || b.trial_used;
  const grace = b.grace_until;
  const partner = b.partner || {};
  const days = b.trial_days || 7;
  const title = grace ? "Keep your Ink Genius access" : lapsed ? "Renew your membership" : `Start your ${days}-day free trial`;
  const sub = grace
    ? `Your free access runs until ${fmt(grace)}. Add a card now and you won't be charged until then.`
    : lapsed
      ? "Your membership isn't active. Renew to unlock your consultations, kit and reports again."
      : `Full access for ${days} days. A card is required to start, and you won't be charged until your trial ends. Cancel anytime before then.`;
  return (
    <div className="min-h-screen bg-[#FDFBF7] px-6 py-8 sm:px-10" data-testid="subscribe-page">
      <div className="mx-auto mb-6 flex max-w-5xl items-center gap-4">
        <Link to="/welcome" aria-label="Ink Genius home" data-testid="subscribe-logo-link"><Wordmark /></Link>
        <Link to="/welcome" data-testid="subscribe-home-btn" className="inline-flex min-h-[40px] items-center gap-2 rounded-full border border-[#E6DBCF] bg-white/70 px-4 text-sm font-medium text-[#2C1810] transition-colors hover:border-[#C5A059] hover:bg-[#FFFBF2]">
          <Home size={15} /> Home
        </Link>
      </div>
      <div className="mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-2">
        <div>
          <h1 className="font-serif text-4xl font-semibold leading-tight text-[#2C1810] sm:text-5xl" data-testid="subscribe-title">{title}</h1>
          <p className="mt-3 max-w-md text-[15px] leading-relaxed text-[#6E5549]" data-testid="subscribe-sub">{sub}</p>
          {partner.pending_end_date && (
            <p className="mt-4 rounded-xl border border-[#E3D3B5] bg-[#F7EEDD] px-4 py-3 text-sm text-[#6B5434]" data-testid="subscribe-partner-pending">
              <b>Verify your email to activate partner access.</b> Your PMU Accelerator code is saved and will unlock Ink Genius through {fmtDate(partner.pending_end_date)} as soon as you click the link we emailed you.
            </p>
          )}
          {partner.expired && !partner.pending_end_date && (
            <p className="mt-4 rounded-xl border border-[#E3D3B5] bg-[#FFFBF2] px-4 py-3 text-sm text-[#4A3B32]" data-testid="subscribe-partner-expired">
              Your PMU Accelerator partner access ended on {fmtDate(partner.end_date)}. Your kit and account are saved — choose a plan below to keep using Ink Genius.
            </p>
          )}
          {params.get("canceled") && <p className="mt-4 rounded-xl bg-[#FDF2F0] px-4 py-3 text-sm text-[#8C2A2A]" data-testid="subscribe-canceled">Checkout was canceled. You can start again anytime.</p>}
          <div className="mt-8"><Perks /></div>
          {!partner.pending_end_date && (
            <div className="mt-8 max-w-md" data-testid="subscribe-partner-section">
              <PartnerCodeField />
            </div>
          )}
        </div>
        <div className="rounded-3xl border border-[#E6DBCF] bg-white/70 p-8 shadow-[0_24px_60px_rgba(44,24,16,0.08)]" data-testid="subscribe-card">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#9C6218]">Ink Genius Membership</p>
          <div className="mt-5 grid gap-3" role="radiogroup" aria-label="Choose a plan">
            {[["yearly", "Yearly", "$249.50", "/ year", "About $20.79/month · 2 months free"], ["monthly", "Monthly", "$24.95", "/ month", "Billed monthly"]].map(([v, l, p, u, note]) => (
              <button key={v} type="button" role="radio" aria-checked={plan === v} onClick={() => setPlan(v)} data-testid={`subscribe-plan-${v}`}
                className={`relative flex items-center justify-between rounded-2xl border-2 px-5 py-4 text-left transition-colors ${plan === v ? "border-[#C5A059] bg-[#FFFBF2]" : "border-[#EFE6DA] hover:border-[#E3D3B5]"}`}>
                <span>
                  <span className="block text-sm font-semibold text-[#2C1810]">{l}</span>
                  <span className="block text-xs text-[#6E5549]">{note}</span>
                </span>
                <span className="flex items-baseline gap-1 text-[#2C1810]"><span className="font-serif text-3xl font-semibold" data-testid={`subscribe-price-${v}`}>{p}</span><span className="text-xs text-[#6E5549]">{u}</span></span>
                {v === "yearly" && <span className="absolute -top-2.5 right-4 rounded-full bg-[#2C1810] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#C5A059]">Best value</span>}
              </button>
            ))}
          </div>
          <p className="mt-3 text-sm text-[#6E5549]" data-testid="subscribe-terms">{grace ? `First charge on ${fmt(grace)}` : lapsed ? "Cancel anytime" : `${days} days free, then ${plan === "yearly" ? "$249.50/year" : "$24.95/month"} · cancel anytime`}</p>
          {partner.pending_end_date ? (
            <button onClick={async () => { try { await api.post("/auth/resend-verification"); toast.success("Verification email sent. Check your inbox."); } catch (e) { toast.error(errMsg(e)); } }}
              data-testid="subscribe-resend-verification-btn" className="mt-8 inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#2C1810] text-base font-semibold text-[#FDFBF7] hover:bg-[#3D2317]">
              Resend verification email
            </button>
          ) : (
          <button onClick={() => goCheckout(setBusy, plan)} disabled={busy} data-testid="subscribe-checkout-btn" className="mt-8 inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#C5A059] text-base font-semibold text-[#2C1810] transition-colors hover:bg-[#d4b273] disabled:opacity-60">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />} {busy ? "Opening secure checkout…" : grace ? "Add card & keep access" : lapsed ? "Renew membership" : "Start free trial"}
          </button>
          )}
          <p className="mt-3 text-center text-xs text-[#A88B7D]"><Link to="/pricing" className="font-semibold text-[#9C6218] underline decoration-[#C5A059]/60 underline-offset-2 hover:text-[#2C1810]" data-testid="subscribe-compare-link">Compare plans</Link> · Secure checkout by Stripe. Prices in USD; tax may apply.</p>
          <div className="mt-6 flex items-center justify-between border-t border-[#EFE6DA] pt-4 text-xs text-[#6E5549]">
            {grace ? <Link to="/" className="underline-offset-2 hover:underline" data-testid="subscribe-later-link">Not now, continue to the app</Link> : <Link to="/profile" className="underline-offset-2 hover:underline" data-testid="subscribe-account-link">Account settings</Link>}
            <button onClick={logout} className="inline-flex items-center gap-1 hover:text-[#2C1810]" data-testid="subscribe-signout-btn"><LogOut size={13} /> Sign out</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("checking");
  const tries = useRef(0);
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return setState("missing");
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/sync/${sid}`);
        if (data.has_access && ["trialing", "active", "past_due"].includes(data.status)) {
          const u = await refreshUser();
          if (!stop) {
            setState("done");
            setTimeout(() => nav(needsOnboarding(u) ? "/onboarding" : "/", { replace: true }), 1400);
          }
          return;
        }
      } catch (e) {
        console.warn("Membership sync not ready yet, retrying", e);
      }
      tries.current += 1;
      if (!stop) (tries.current < 12 ? setTimeout(poll, 2000) : setState("slow"));
    };
    poll();
    return () => { stop = true; };
  }, [params, refreshUser, nav]);
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#FDFBF7] px-6 text-center" data-testid="payment-success-page">
      <InkCharacter size="md" />
      {state === "checking" && <><Loader2 className="mt-8 animate-spin text-[#C5A059]" /><p className="mt-4 text-[#4A3B32]" data-testid="payment-checking">Confirming your membership…</p></>}
      {state === "done" && <><h1 className="mt-8 font-serif text-4xl font-semibold text-[#2C1810]" data-testid="payment-done">Welcome to Ink Genius</h1><p className="mt-2 text-[#6E5549]">Your membership is active. Taking you in…</p></>}
      {(state === "slow" || state === "missing") && (
        <><p className="mt-8 max-w-sm text-[#4A3B32]" data-testid="payment-slow">We're still waiting for Stripe to confirm. This can take a minute — refresh this page, or check Membership in your account.</p>
          <Link to="/billing" className="mt-4 text-sm font-semibold text-[#9C6218] underline">Go to Membership</Link></>
      )}
    </div>
  );
}

const STATUS = { trialing: "Free trial", active: "Active", past_due: "Payment issue", canceled: "Canceled", unpaid: "Unpaid", incomplete: "Incomplete", none: "No membership" };

export function BillingPage() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const b = user?.billing || {};
  if (user?.role === "admin") return <div className="mx-auto max-w-3xl px-6 py-14"><h1 className="font-serif text-4xl font-semibold text-[#2C1810]">My Plan</h1><p className="mt-3 text-[#6E5549]" data-testid="billing-owner-note">The owner account has full access and is never billed.</p><AccountSections b={b} owner /></div>;
  const portal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  const amt = money(b.plan_amount || (b.plan === "yearly" ? 249.5 : 24.95));
  const line = b.status === "trialing" ? `Your free trial ends ${fmt(b.trial_end)}. ${b.cancel_at_period_end ? "Your membership won't renew." : `You'll be charged ${amt} then, and every ${per(b.plan)} after.`}`
    : b.status === "active" ? (b.cancel_at_period_end ? `Your membership ends ${fmt(b.current_period_end)} and won't renew.` : `Next charge of ${amt} on ${fmt(b.current_period_end)}.`)
      : b.status === "past_due" ? "Your last payment didn't go through. Update your card to keep access."
        : b.grace_until ? `Free launch access until ${fmt(b.grace_until)}. Add a card to keep going after that.` : "You don't have an active membership.";
  return (
    <div className="mx-auto max-w-3xl px-6 py-10 sm:px-10 lg:py-14" data-testid="billing-page">
      <h1 className="font-serif text-4xl font-semibold text-[#2C1810] sm:text-5xl">My Plan</h1>
      {b.partner?.active && <div className="mt-8"><PartnerPlanCard partner={b.partner} /></div>}
      {b.partner?.pending_end_date && <p className="mt-8 rounded-2xl bg-[#F7EEDD] p-5 text-sm text-[#6B5434]" data-testid="billing-partner-pending"><b>Verify your email to activate partner access</b> through {fmtDate(b.partner.pending_end_date)}.</p>}
      {b.partner?.expired && !b.partner?.active && <p className="mt-8 rounded-2xl bg-[#FFFBF2] p-5 text-sm text-[#4A3B32]" data-testid="billing-partner-expired">PMU Accelerator Partner Access ended {fmtDate(b.partner.end_date)}. Your kit and account are saved.</p>}
      {(!b.partner?.active || ["trialing", "active", "past_due"].includes(b.status)) && (
      <div className="mt-8 rounded-3xl border border-[#E6DBCF] bg-white/70 p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#9C6218]" data-testid="billing-plan">Ink Genius Membership · {b.plan === "yearly" ? "Yearly · $249.50/year" : b.plan ? "Monthly · $24.95/month" : "$24.95/month or $249.50/year"}</p>
            <p className="mt-2 font-serif text-3xl font-semibold text-[#2C1810]" data-testid="billing-status">{b.grace_until && b.status === "none" ? "Launch trial" : STATUS[b.status] || b.status}</p>
          </div>
        </div>
        <p className="mt-3 text-sm text-[#4A3B32]" data-testid="billing-line">{line}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          {b.has_customer && ["trialing", "active", "past_due"].includes(b.status) ? (
            <button onClick={portal} disabled={busy} data-testid="billing-portal-btn" className="inline-flex h-12 items-center gap-2 rounded-full bg-[#2C1810] px-6 text-sm font-semibold text-[#FDFBF7] hover:bg-[#3D2317] disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Manage billing, card or cancel
            </button>
          ) : (
            <Link to="/subscribe" data-testid="billing-subscribe-btn" className="inline-flex h-12 items-center gap-2 rounded-full bg-[#C5A059] px-6 text-sm font-semibold text-[#2C1810] hover:bg-[#d4b273]"><CreditCard size={16} /> {b.grace_until ? "Add card & keep access" : "Start membership"}</Link>
          )}
        </div>
      </div>
      )}
      {!b.partner?.active && !b.partner?.pending_end_date && !["trialing", "active", "past_due"].includes(b.status) && <div className="mt-6"><PartnerCodeField /></div>}
      <AccountSections b={b} onPortal={portal} busy={busy} />
    </div>
  );
}

export function TrialBanner() {
  const { user } = useAuth();
  const b = user?.billing;
  if (!b || user.role === "admin" || b.partner?.active) return null;
  const days = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));
  if (b.status === "none" && b.grace_until) {
    return <div className="flex flex-wrap items-center gap-3 bg-[#2C1810] px-6 py-2.5 text-sm text-[#FDFBF7]" data-testid="trial-banner"><span className="flex-1">Free launch access: {days(b.grace_until)} day{days(b.grace_until) === 1 ? "" : "s"} left.</span><Link to="/subscribe" className="font-semibold text-[#C5A059] underline-offset-2 hover:underline" data-testid="trial-banner-cta">Add card to keep access</Link></div>;
  }
  if (b.status === "trialing" && b.trial_end) {
    return <div className="flex flex-wrap items-center gap-3 bg-[#F6F1EA] px-6 py-2.5 text-sm text-[#4A3B32]" data-testid="trial-banner"><span className="flex-1">Free trial: {days(b.trial_end)} day{days(b.trial_end) === 1 ? "" : "s"} left{b.cancel_at_period_end ? " · won't renew" : ` · then ${money(b.plan_amount || 24.95)}/${per(b.plan)}`}.</span><Link to="/billing" className="font-semibold text-[#9C6218] underline-offset-2 hover:underline">Manage</Link></div>;
  }
  if (b.status === "past_due") {
    return <div className="flex flex-wrap items-center gap-3 bg-[#F9ECEC] px-6 py-2.5 text-sm text-[#8C2A2A]" data-testid="trial-banner"><span className="flex-1">Your last payment didn't go through.</span><Link to="/billing" className="font-semibold underline">Update card</Link></div>;
  }
  return null;
}
